import {
  InstancedMesh,
  Matrix4,
  InstancedBufferAttribute,
  type BufferGeometry,
  type Material,
} from 'three';
import type { LinearRGB } from '../../core/color';
import type { Rng } from '../../core/rng';
import type { Vec2 } from '../../geo/types';
import type { BuildContext, FeatureOutput, WorldFeature } from '../types';
import { edgeIdFor } from '../mesh/attributes';
import { layoutToWorld, worldToLayout } from '../../core/coords';
import type { ShipType } from './shipTemplates';
import {
  placeAnchorageShips,
  painterViewHeading,
  shipDeal,
  type PlacedShip,
  type PalisadeSegment,
} from './shipPlacement';
import { placeBerthShips, type BerthData } from './shipBerths';
import { createShipLanterns } from './shipLanterns';
import { harbourBarriers } from './harbourBarriers';
import { placeMooredBoats, deckBox } from './mooredBoats';
import { withPaintedBridgeBerths } from './paintedBridgeBerths';
import {
  placePiles,
  buildPalisadeBeam,
  extractPalisadeSegments,
  buildPileTemplate,
  PALISADE_PAINTER_SCALE_H,
  PALISADE_PAINTER_SCALE_V,
  type PalisadeData,
  type PalisadeSpan,
} from './palisade';
import {
  BOOM_HOUSES,
  BOOM_HOUSE_PAINTER_SCALE,
  boomHouseMatrix,
  boomHousePiles,
  buildBoomHouse,
  placeBoomHouses,
  type PlacedBoomHouse,
} from './boomHouse';
import {
  BLOCKHOUSES,
  BLOCKHOUSE_PAINTER_SCALE,
  blockhouseMatrix,
  buildBlockhouse,
  hiddenByBlockhouse,
  placeBlockhouses,
  type PlacedBlockhouse,
} from './blockhouse';
import type { ShipColors } from './shipGeometry';
import { buildYardStocks } from './stocks';
import { createShipMeshes } from './shipMeshes';
import { harbourZoneOf, frameRegionOf, withSalutes, endAwayFromView } from './harbourZones';
import { buildHarbourTrade } from './tradeFeature';
import { shipColors } from './shipColors';

interface Pile {
  readonly position: Vec2;
  readonly topY: number;
  readonly edgeId: number;
}

/** A stretch of a row that a boom house leaves open or a blockhouse stands astride. */
interface RowOpening {
  readonly palisadeId: string;
  readonly opening: PalisadeSpan;
}

/* Beams go into the infrastructure chunks; the piles come back for one instanced mesh. A palisade
   is one object to the outline pass, beam and piles alike: an id per pile inked every pile edge
   and turned the row into a solid black band. */
function buildPalisades(
  ctx: BuildContext,
  palisades: readonly PalisadeData[],
  rowOpenings: readonly RowOpening[],
  hullColor: LinearRGB,
  rng: Rng
): Pile[] {
  return palisades.flatMap((palisade, index) => {
    const edgeId = edgeIdFor(index);
    const openings = rowOpenings.filter((o) => o.palisadeId === palisade.id).map((o) => o.opening);
    const piles = placePiles(palisade, rng.fork(palisade.id), openings);
    const beamBuilder = ctx.chunks.builderAt('infrastructure', palisade.path[0]!);
    buildPalisadeBeam(beamBuilder, palisade, hullColor, edgeId, rng.next(), openings);
    return piles.map((pile) => ({ ...pile, edgeId }));
  });
}

/* A boom house and the piles under its corners, or a blockhouse, are one object with the row they
   stand on, as its beam and piles are. */
function rowEdgeIds(
  houses: readonly { readonly palisadeId: string }[],
  palisades: readonly PalisadeData[]
): readonly number[] {
  return houses.map((house) => edgeIdFor(palisades.findIndex((p) => p.id === house.palisadeId)));
}

function boomHouseCornerPiles(
  houses: readonly PlacedBoomHouse[],
  edgeIds: readonly number[]
): Pile[] {
  return houses.flatMap((house, index) =>
    boomHousePiles(house).map((position) => ({ position, topY: 1, edgeId: edgeIds[index]! }))
  );
}

/* A pile inside a blockhouse's wall as the painter view draws it would stand in its open top
   there; it folds away in that view alone. */
function pileMesh(
  piles: readonly Pile[],
  blockhouses: readonly PlacedBlockhouse[],
  hullColor: LinearRGB,
  material: Material,
  rng: Rng
): InstancedMesh {
  const geometry = buildPileTemplate(hullColor, edgeIdFor(1), rng.next());
  const mesh = new InstancedMesh(geometry, material, piles.length);
  mesh.frustumCulled = false;
  const matrix = new Matrix4();
  piles.forEach((pile, i) => {
    const [x, , z] = layoutToWorld(pile.position, 0);
    mesh.setMatrixAt(i, matrix.makeScale(1, pile.topY, 1).setPosition(x, 0, z));
  });
  mesh.instanceMatrix.needsUpdate = true;
  const edgeIds = piles.map((pile) => pile.edgeId);
  const scales = piles.flatMap((pile) =>
    hiddenByBlockhouse(blockhouses, pile.position)
      ? [0, 0]
      : [PALISADE_PAINTER_SCALE_H, PALISADE_PAINTER_SCALE_V]
  );
  geometry.setAttribute(
    'aInstanceEdgeId',
    new InstancedBufferAttribute(Float32Array.from(edgeIds), 1)
  );
  geometry.setAttribute(
    'aPainterScale',
    new InstancedBufferAttribute(Float32Array.from(scales), 2)
  );
  return mesh;
}

interface RowHouses {
  readonly name: string;
  readonly geometry: () => BufferGeometry;
  readonly matrices: readonly Matrix4[];
  readonly edgeIds: readonly number[];
  readonly painterScale: readonly [number, number];
}

/* Boom houses and blockhouses: one instanced mesh a kind, drawn at the ships' painter scale. */
function rowHouseMesh(houses: RowHouses, material: Material): InstancedMesh {
  const { matrices, edgeIds } = houses;
  const geometry = houses.geometry();
  const mesh = new InstancedMesh(geometry, material, matrices.length);
  mesh.name = houses.name;
  mesh.frustumCulled = false;
  matrices.forEach((matrix, i) => {
    mesh.setMatrixAt(i, matrix);
  });
  mesh.instanceMatrix.needsUpdate = true;
  const scales = matrices.flatMap(() => houses.painterScale);
  geometry.setAttribute(
    'aInstanceEdgeId',
    new InstancedBufferAttribute(Float32Array.from(edgeIds), 1)
  );
  geometry.setAttribute(
    'aPainterScale',
    new InstancedBufferAttribute(Float32Array.from(scales), 2)
  );
  return mesh;
}

interface Fleet {
  readonly ships: readonly PlacedShip[];
  readonly moored: number;
  readonly berthed: number;
}

function berthsOf(ctx: BuildContext): readonly BerthData[] {
  return ctx.layout.harbour.berths.map((b) => ({
    id: b.id,
    position: b.pos,
    craft: b.craft,
    sailSet: b.sailSet,
    salute: b.salute,
    ...(b.bow ? { bow: b.bow } : {}),
  }));
}

/* The boats moored along the banks come first, then every vessel the painting shows at its
   berth, then the ships anchored on the water beyond the painter frame. Every vessel keeps clear
   of all placed before it. A ship beyond the frame fires away from it, so the frame shows the
   painted puffs alone. */
function placeFleet(ctx: BuildContext, segments: readonly PalisadeSegment[], rng: Rng): Fleet {
  const { layout, site } = ctx;
  const viewHeading = painterViewHeading(ctx.painterView);
  const eye = worldToLayout(ctx.painterView.position);
  const warn = (message: string): void => {
    ctx.warn(message);
  };
  const quayside = { waterIndex: site.waterIndex, bridges: site.bridges.map(deckBox) };
  /* One deal for the whole fleet, so that its ships come out in the painted shares. */
  const deal = shipDeal(rng.fork('ship types'));
  const moored = layout.harbour.moorings.reduce<readonly PlacedShip[]>(
    (placed, m) => [
      ...placed,
      ...placeMooredBoats(
        withPaintedBridgeBerths(m, layout.bridges),
        quayside,
        placed,
        rng.fork(m.id),
        warn,
        deal
      ),
    ],
    []
  );
  const clearances = { waterIndex: site.waterIndex, palisades: segments };
  const berths = rng.fork('berths');
  const berthed = placeBerthShips(
    berthsOf(ctx),
    clearances,
    moored,
    viewHeading,
    berths,
    warn,
    deal
  );
  const ships = layout.harbour.anchorages.reduce<readonly PlacedShip[]>(
    (placed, a) => {
      const zone = harbourZoneOf(a.id);
      const region = frameRegionOf(a.id);
      const anchorage = { id: a.id, polygon: a.polygon, density: a.density, zone, region };
      const fork = rng.fork(a.id);
      const found = placeAnchorageShips(
        anchorage,
        site.waterIndex,
        segments,
        placed,
        viewHeading,
        fork,
        warn,
        deal
      );
      const away =
        region === 'beyondFrame'
          ? (ship: PlacedShip) => endAwayFromView(ship, eye, viewHeading)
          : undefined;
      return [...placed, ...withSalutes(found, zone, fork.fork('salutes'), away)];
    },
    [...moored, ...berthed]
  );
  return { ships, moored: moored.length, berthed: berthed.length };
}

function palisadesOf(ctx: BuildContext): PalisadeData[] {
  return ctx.layout.harbour.palisades.map((p) => ({
    id: p.id,
    path: p.path,
    pileSpacing: p.pileSpacing,
  }));
}

/** The vessels of the harbour, moored and at anchor, as the feature places them. */
export function fleetOf(ctx: BuildContext): readonly PlacedShip[] {
  const segments = palisadesOf(ctx).flatMap((palisade) => extractPalisadeSegments(palisade));
  return placeFleet(ctx, segments, ctx.rng.fork('ships')).ships;
}

function fleetCounts({ ships, moored, berthed }: Fleet): Record<string, number> {
  const ofType = (type: ShipType): number => ships.filter((s) => s.type === type).length;
  return {
    ships: ships.length,
    anchorageShips: ships.length - moored - berthed,
    berthShips: berthed,
    mooringShips: moored,
    kraecks: ofType('kraeck'),
    hulks: ofType('hulk'),
    barks: ofType('bark'),
    boeiers: ofType('boeier'),
    rowingBoats: ofType('rowing'),
    praams: ofType('praam'),
  };
}

interface HarbourWorks {
  readonly objects: readonly InstancedMesh[];
  readonly counts: Readonly<Record<string, number>>;
  readonly houses: readonly PlacedBoomHouse[];
  readonly blockhouses: readonly PlacedBlockhouse[];
}

/* The rows, their piles, and the houses standing on them. The order the rng is drawn in keeps
   every pile's height and every seed the rows had before the blockhouse stood among them. */
function harbourWorks(
  ctx: BuildContext,
  palisades: readonly PalisadeData[],
  colors: ShipColors,
  material: Material,
  rng: Rng
): HarbourWorks {
  const warn = (message: string): void => {
    ctx.warn(message);
  };
  const houses = placeBoomHouses(BOOM_HOUSES, palisades, warn);
  const blockhouses = placeBlockhouses(BLOCKHOUSES, palisades, warn);
  const houseIds = rowEdgeIds(houses, palisades);
  const piles = [
    ...buildPalisades(ctx, palisades, [...houses, ...blockhouses], colors.hull, rng),
    ...boomHouseCornerPiles(houses, houseIds),
  ];
  const pileMeshes =
    piles.length > 0 ? [pileMesh(piles, blockhouses, colors.hull, material, rng)] : [];
  const houseSeed = rng.fork('boom houses').next();
  const blockIds = rowEdgeIds(blockhouses, palisades);
  const blockSeed = rng.fork('blockhouses').next();
  const kinds: RowHouses[] = [
    {
      name: 'boom houses',
      geometry: () => buildBoomHouse(colors.hull, colors.port, houseIds[0]!, houseSeed),
      matrices: houses.map(boomHouseMatrix),
      edgeIds: houseIds,
      painterScale: BOOM_HOUSE_PAINTER_SCALE,
    },
    {
      name: 'blockhouses',
      geometry: () => buildBlockhouse(colors.hull, colors.port, blockIds[0]!, blockSeed),
      matrices: blockhouses.map(blockhouseMatrix),
      edgeIds: blockIds,
      painterScale: BLOCKHOUSE_PAINTER_SCALE,
    },
  ];
  const houseMeshes = kinds
    .filter((kind) => kind.matrices.length > 0)
    .map((kind) => rowHouseMesh(kind, material));
  return {
    objects: [...pileMeshes, ...houseMeshes],
    houses,
    blockhouses,
    counts: {
      piles: piles.length,
      palisades: palisades.length,
      boomHouses: houses.length,
      blockhouses: blockhouses.length,
    },
  };
}

export const shipsFeature: WorldFeature = {
  id: 'ships',
  label: 'Ships and palisade',
  weight: 5,

  build(ctx: BuildContext): FeatureOutput {
    const shipRng = ctx.rng.fork('ships');
    const palisadeRng = ctx.rng.fork('palisade');
    const material = ctx.materials.painted({ instanced: true });
    const colors = shipColors(ctx.palette);

    const palisades = palisadesOf(ctx);
    const works = harbourWorks(ctx, palisades, colors, material, palisadeRng);

    const segments = palisades.flatMap((palisade) => extractPalisadeSegments(palisade));
    const placed = placeFleet(ctx, segments, shipRng);
    const fleet = createShipMeshes(
      placed.ships,
      colors,
      ctx.materials.painted({ instanced: true, clothWind: true }),
      shipRng
    );
    const { hull, wale, rail, port, rigging } = colors;
    const palette = { hull, wale, rail, port, rope: rigging };
    const stocks = buildYardStocks(ctx.chunks, palisades.length, palette, ctx.rng.fork('stocks'));

    const lanterns = createShipLanterns(placed.ships, colors.rail, material, shipRng);
    const afloat = harbourBarriers({ ships: placed.ships, palisades, ...works });
    const trade = buildHarbourTrade(ctx, placed.ships, afloat);

    return {
      objects: [...works.objects, ...fleet.meshes, ...lanterns.meshes, ...trade.objects],
      obstacles: trade.obstacles,
      afloat,
      animated: [...fleet.animated, lanterns.animated, ...trade.animated],
      counts: { ...works.counts, stocks, ...fleetCounts(placed), ...trade.counts },
    };
  },
};
